import {
    GridCallbackDetails,
    GridFilterModel,
    GRID_DETAIL_PANEL_TOGGLE_FIELD,
    GRID_CHECKBOX_SELECTION_FIELD,
} from '@mui/x-data-grid-pro'

import {
    ExtendedGridColDef,
    FilterOperatorType,
    ExtendedGridFilterItem,
    ExtendedGridFilterModel,
} from '../SkyBaseDataGrid.types'

export const getIncludeFiltersFromFilterModel = (filterModel: GridFilterModel,
    exceptFilterName?: string) => {
    const includeFilters = Object.fromEntries(filterModel?.items
        .filter((filter) => {
            return (Boolean(filter.value))
                && (filter.field !== exceptFilterName)
                && (filter.operator === FilterOperatorType.EqualsIncludeFilter)
        })
        .map((filter) => {
            return [
                filter.field,
                filter.value,
            ]
        }))

    return includeFilters
}

export const getOtherFilterForBodyFromFilterModel = (filterModel: GridFilterModel) => {
    const result = Object.fromEntries(filterModel?.items
        .filter((filter) => {
            return (Boolean(filter.value))
                && (filter.operator === FilterOperatorType.DataFilter)
        })
        .map((filter) => {
            return [
                filter.field,
                filter.value,
            ]
        }))

    return result
}

export const getFiltersFromFilterModel = (filterModel: GridFilterModel) => {
    const includeFilters = getIncludeFiltersFromFilterModel(filterModel)
    const otherFilterForBody = getOtherFilterForBodyFromFilterModel(filterModel)

    return {
        searchAll: filterModel?.quickFilterValues?.join(' ') || undefined,
        includeFilters,
        ...otherFilterForBody,
    }
}

export const getVisibleAndToggableColumns = (columns: ExtendedGridColDef[]) => {
    return columns.filter((column) => {
        return !column.hiddenFromColumns
                && column.field !== GRID_DETAIL_PANEL_TOGGLE_FIELD
                && column.field !== GRID_CHECKBOX_SELECTION_FIELD
    }).map((column) => {
        return column.field
    })
}

// eslint-disable-next-line max-len
export const getHiddenPredefinedFiltersDefinitions = (columns: ExtendedGridColDef[]): ExtendedGridColDef[] => {
    return columns.filter((column) => {
        return column.hiddenFromColumns
            && column.filterOperators[0].value === FilterOperatorType.DataFilter
    })
}

export const getNotHiddenDefinitions = (columns: ExtendedGridColDef[]): ExtendedGridColDef[] => {
    return columns.filter((column) => {
        return !column.hiddenFromColumns
    })
}

const FILTER_ID_PREFIX = '66666'

export const getPredefinedDataFilter = (columns: ExtendedGridColDef[]) => {
    return getHiddenPredefinedFiltersDefinitions(columns)
        .map((column, index) => {
            return {
                field: column.field,
                id: FILTER_ID_PREFIX + index,
                operator: column.filterParams?.operator,
                value: column.filterParams?.defaultValue,
                defaultValue: column.filterParams?.defaultValue,
            }
        })
}

export const hideHiddenColumnsForFilter = (columns: ExtendedGridColDef[]) => {
    return Object.fromEntries(getHiddenPredefinedFiltersDefinitions(columns)
        .map((filter) => {
            return [
                filter.field,
                false,
            ]
        }))
}

type FilterModelChangeReason = 'upsertFilterItem' | 'upsertFilterItems'
    | 'deleteFilterItem' | 'changeLogicOperator' | 'restoreState' | 'removeAllFilterItems'

export const correctionFilterModel = (model: ExtendedGridFilterModel, details: GridCallbackDetails<'filter'>, prevFilterModel: GridFilterModel) => {
    const reason: FilterModelChangeReason = details?.reason

    // action when filter's value or column was changed
    if (reason === 'upsertFilterItem') {
        return cleanUpFilterValueOnColumnChange(model, prevFilterModel)
    }
    // when button clear all filter was clicked
    if (reason === 'removeAllFilterItems') {
        return resetFilterModelOnRemoveAll(model)
    }
    return model
}

// clean up filter's value if filter's column was changed
const cleanUpFilterValueOnColumnChange = (model: ExtendedGridFilterModel,
    prevFilterModel: GridFilterModel) => {
    const prevColumnFields = prevFilterModel.items.map((i) => {
        return i.field
    })
    const newColumnFields = model.items.map((i) => {
        return i.field
    })
    const hasFieldColumnChanged = prevColumnFields.length === newColumnFields.length
        && prevColumnFields.some((field, index) => {
            return field !== newColumnFields[index]
        })

    if (hasFieldColumnChanged) {
        const changedFilterIndex = prevColumnFields.findIndex((field, index) => {
            return field !== newColumnFields[index]
        })

        return {
            ...model,
            items: [...model.items.map((item: ExtendedGridFilterItem, index) => {
                return {
                    ...item,
                    value: index === changedFilterIndex ? item.defaultValue : item.value,
                }
            })],
        }
    }
    return model
}

// reset to default value hidden predefined filters
export const resetFilterModelOnRemoveAll = (model: ExtendedGridFilterModel) => {
    return {
        ...model,
        items: [...model.items.map((item: ExtendedGridFilterItem) => {
            return {
                ...item,
                value: item.defaultValue,
            }
        })],
    }
}

/*  customize what we consider active filters */
export const getActiveFilters = (filterModel: ExtendedGridFilterModel) => {
    return filterModel.items.filter((filter: ExtendedGridFilterItem) => {
        const {
            value,
            defaultValue,
            operator,
        } = filter

        /* special dataFilters considered not active by default, for example archived filter */
        if (value === defaultValue && operator === FilterOperatorType.DataFilter) {
            return false
        }

        return value !== undefined && value !== null && value !== ''
    })
}
